/**
 * ReloadPrompt
 * Detects when a new version of the PWA Service Worker is available.
 * Automatically triggers skipWaiting + reload so users always get
 * the latest version without manually clearing their browser cache.
 *
 * Only active in production — the PWA plugin (and its virtual module
 * "virtual:pwa-register/react") is disabled in dev mode.
 */

import { useEffect, useState } from 'react';

export default function ReloadPrompt() {
  const [swRegistration, setSwRegistration] = useState<ServiceWorkerRegistration | null>(null);

  useEffect(() => {
    // Only run in production where service workers are available
    if (!('serviceWorker' in navigator)) return;

    navigator.serviceWorker.ready.then((registration) => {
      setSwRegistration(registration);
    });

    // Listen for new SW waiting to activate
    const handleControllerChange = () => {
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);
    return () => {
      navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
    };
  }, []);

  // Periodically check for updates every 60 seconds
  useEffect(() => {
    if (!swRegistration) return;

    const interval = setInterval(() => {
      swRegistration.update();
    }, 60 * 1000);

    return () => clearInterval(interval);
  }, [swRegistration]);

  // Check if there's a waiting SW and tell it to activate
  useEffect(() => {
    if (!swRegistration?.waiting) return;
    swRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });
  }, [swRegistration]);

  // Also listen for new SW entering "waiting" state
  useEffect(() => {
    if (!swRegistration) return;

    const onUpdateFound = () => {
      const newSW = swRegistration.installing;
      if (!newSW) return;

      newSW.addEventListener('statechange', () => {
        if (newSW.state === 'installed' && navigator.serviceWorker.controller) {
          // New version ready — skip waiting to trigger controllerchange → reload
          newSW.postMessage({ type: 'SKIP_WAITING' });
        }
      });
    };

    swRegistration.addEventListener('updatefound', onUpdateFound);
    return () => swRegistration.removeEventListener('updatefound', onUpdateFound);
  }, [swRegistration]);

  return null;
}
